import { useState } from 'react';
import { toast } from 'sonner';
import { Upload, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { adsAPI } from '@/utils/api';

const CONTACTS = [
  { value: 'discord', label: 'Discord username', placeholder: 'e.g. goku#0001' },
  { value: 'email', label: 'Email address', placeholder: 'you@example.com' },
  { value: 'username', label: 'Dragon TV username', placeholder: 'Your Dragon TV name' },
];
const inputCls = 'w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-primary';

export const AdRequestModal = ({ open, onOpenChange, defaultType = 'banner' }) => {
  const [form, setForm] = useState({ ad_type: defaultType, description: '', link_url: '', contact_method: 'discord', contact_value: '' });
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const contact = CONTACTS.find((c) => c.value === form.contact_method);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Please attach the image or video for your ad');
    if (file.size > 50 * 1024 * 1024) return toast.error('File too large (max 50MB)');
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append('file', file);
    try {
      setProgress(0);
      await adsAPI.submitRequest(fd, (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100)));
      toast.success("Ad request sent! We'll contact you about payment.");
      setForm({ ...form, description: '', link_url: '', contact_value: '' });
      setFile(null);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send ad request');
    } finally {
      setProgress(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="ad-request-modal">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase italic">Advertise on Dragon TV</DialogTitle>
          <DialogDescription className="text-gray-400">Send your ad and we'll reach out about payment before it goes live.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between rounded-lg border border-primary/40 bg-primary/10 px-3 py-2" data-testid="ad-request-price">
          <span className="text-sm text-gray-200">Ad space price</span>
          <span className="font-heading text-lg text-white">$5 <span className="text-xs font-sans text-gray-300">per 15 days</span></span>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {[['banner', 'Banner ad'], ['video', 'Video ad (before episodes)']].map(([v, l]) => (
              <button type="button" key={v} onClick={() => setForm({ ...form, ad_type: v })} data-testid={`ad-request-type-${v}`}
                className={`py-2 px-2 rounded-lg text-xs font-bold border transition-colors ${form.ad_type === v ? 'bg-primary border-primary text-white' : 'border-white/10 text-gray-300 hover:border-white/30'}`}>
                {l}
              </button>
            ))}
          </div>
          <textarea required rows={3} value={form.description} onChange={set('description')} placeholder="What is the ad for?" className={inputCls} data-testid="ad-request-description" />
          <input required type="url" value={form.link_url} onChange={set('link_url')} placeholder="Link people go to when they click (https://...)" className={inputCls} data-testid="ad-request-link" />
          <div className="grid grid-cols-3 gap-2">
            <select value={form.contact_method} onChange={set('contact_method')} className={`${inputCls} col-span-1`} data-testid="ad-request-contact-method">
              {CONTACTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <input required value={form.contact_value} onChange={set('contact_value')} placeholder={contact.placeholder} className={`${inputCls} col-span-2`} data-testid="ad-request-contact-value" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-white/20 hover:border-primary/60 px-3 py-3 text-sm text-gray-300">
            <Upload className="w-4 h-4 text-primary shrink-0" />
            <span className="truncate">{file ? file.name : form.ad_type === 'video' ? 'Upload your video (max 50MB)' : 'Upload image or video (max 50MB)'}</span>
            <input type="file" className="hidden" accept={form.ad_type === 'video' ? 'video/mp4,video/webm,video/quicktime' : 'image/*,video/mp4,video/webm,video/quicktime'} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ad-request-file" />
          </label>
          {progress !== null && (
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden" data-testid="ad-request-progress">
              <div className="h-full bg-primary transition-[width]" style={{ width: `${progress}%` }} />
            </div>
          )}
          <button type="submit" disabled={progress !== null} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary hover:bg-orange-600 disabled:opacity-60 font-bold text-sm transition-colors" data-testid="ad-request-submit">
            <Send className="w-4 h-4" /> {progress !== null ? `Uploading ${progress}%` : 'Send request'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
